import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles, Download } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { TEST_IDS } from "@/constants/testIds";
import LiveTrafficBadge from "@/components/LiveTrafficBadge";

const HERO_TEXTURE = "https://images.pexels.com/photos/10325707/pexels-photo-10325707.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Hero() {
    const { t } = useI18n();
    return (
        <section className="relative pt-32 pb-24 overflow-hidden">
            {/* Background layers */}
            <div className="absolute inset-0 radial-hero pointer-events-none" />
            <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" />
            <div className="absolute inset-0 bg-grain opacity-70 pointer-events-none" />
            <div
                className="absolute -top-24 right-0 w-[720px] h-[720px] pointer-events-none opacity-25 mix-blend-screen"
                style={{
                    backgroundImage: `url(${HERO_TEXTURE})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    maskImage: "radial-gradient(ellipse at center, black 40%, transparent 75%)",
                    WebkitMaskImage: "radial-gradient(ellipse at center, black 40%, transparent 75%)",
                    filter: "hue-rotate(210deg) saturate(1.4)",
                }}
            />
            {/* Floating orbs */}
            <div className="absolute top-40 left-10 w-40 h-40 rounded-full bg-brand-indigo/20 blur-3xl animate-orb-float pointer-events-none" />
            <div className="absolute top-72 right-20 w-56 h-56 rounded-full bg-brand-cyan/15 blur-3xl animate-orb-float pointer-events-none" style={{ animationDelay: "3s" }} />

            <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7">
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            data-testid={TEST_IDS.landing.heroBadge}
                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full badge-cyan text-xs font-medium tracking-wide"
                        >
                            <Sparkles size={12} />
                            <span>{t.landing.eyebrow}</span>
                        </motion.div>

                        <motion.h1
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.05 }}
                            className="mt-6 font-display font-black tracking-tighter text-5xl sm:text-6xl lg:text-7xl leading-[0.95]"
                        >
                            <span className="text-white">{t.landing.heroTitleA}</span>{" "}
                            <span className="text-gradient">{t.landing.heroTitleB}</span>
                        </motion.h1>

                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.15 }}
                            className="mt-6 max-w-xl text-lg text-zinc-400 leading-relaxed"
                        >
                            {t.landing.heroSub}
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.25 }}
                            className="mt-10 flex flex-wrap items-center gap-3"
                        >
                            <Link
                                to="/register"
                                data-testid={TEST_IDS.landing.heroCta}
                                className="btn-cta text-white font-semibold px-6 py-3.5 rounded-full inline-flex items-center gap-2 group"
                            >
                                {t.landing.heroCta}
                                <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </Link>
                            <Link
                                to="/playbook"
                                data-testid="hero-playbook-link"
                                className="text-white/90 font-medium px-6 py-3.5 rounded-full border border-brand-cyan/40 hover:bg-brand-cyan/5 transition inline-flex items-center gap-2"
                            >
                                <Download size={16} /> Playbook gratuit
                            </Link>
                            <a
                                href="#features"
                                data-testid={TEST_IDS.landing.heroSecondary}
                                className="text-white/90 font-medium px-6 py-3.5 rounded-full border border-white/12 hover:border-white/25 hover:bg-white/5 transition"
                            >
                                {t.landing.heroSecondary}
                            </a>
                        </motion.div>

                        <p className="mt-6 text-xs text-zinc-500 tracking-wide">{t.landing.trustedBy}</p>

                        {/* Live traffic — real numbers, no fake */}
                        <div className="mt-6 max-w-2xl">
                            <LiveTrafficBadge />
                        </div>
                    </div>

                    <div className="lg:col-span-5">
                        <HeroPreview />
                    </div>
                </div>
            </div>
        </section>
    );
}

function HeroPreview() {
    // A synthetic "app peek" card — pure CSS/SVG, no external image dependency
    return (
        <motion.div
            initial={{ opacity: 0, y: 30, rotate: -1 }}
            animate={{ opacity: 1, y: 0, rotate: -1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative"
        >
            <div className="glass rounded-2xl p-5 shadow-[0_30px_80px_-20px_rgba(99,102,241,0.5)]">
                <div className="flex items-center justify-between text-xs text-zinc-400 mb-4">
                    <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-brand-cyan animate-pulse-ring" />
                        <span className="font-mono">AI Prospecting · live</span>
                    </div>
                    <span className="font-mono text-brand-cyan">gpt-5.2</span>
                </div>
                <div className="bg-black/40 rounded-lg p-3 font-mono text-[13px] text-zinc-300 border border-white/5">
                    &gt; find CTOs at 20–100 SaaS in Berlin, funded &lt;18 months
                </div>
                <div className="mt-4 space-y-2">
                    {[
                        { n: "Lea Weber", c: "Formia.io", s: 94 },
                        { n: "Kai Ostrowski", c: "Northbeam", s: 89 },
                        { n: "Elena Bauer", c: "Kompanion", s: 87 },
                        { n: "Tomas Havel", c: "Divio Cloud", s: 82 },
                    ].map((l) => (
                        <div key={l.n} className="flex items-center justify-between bg-white/[0.03] hover:bg-white/[0.06] transition rounded-lg px-3 py-2.5 border border-white/5">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-indigo to-brand-violet flex items-center justify-center text-[11px] font-bold">
                                    {l.n.split(" ").map(s => s[0]).join("")}
                                </div>
                                <div>
                                    <div className="text-sm text-white font-medium">{l.n}</div>
                                    <div className="text-xs text-zinc-500">CTO · {l.c}</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="w-16 h-1.5 rounded-full bg-white/10 overflow-hidden">
                                    <div className="h-full bg-gradient-to-r from-brand-cyan to-brand-indigo" style={{ width: `${l.s}%` }} />
                                </div>
                                <span className="text-xs font-mono text-brand-cyan w-8 text-right">{l.s}</span>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="mt-4 flex items-center justify-between text-xs text-zinc-500">
                    <span>328 more found · showing top 4</span>
                    <span className="text-white/70">Push to sequence →</span>
                </div>
            </div>
        </motion.div>
    );
}
